/**
 * Pure runtime logic for the Emjae chat stream.
 *
 * These live outside the provider so the honesty rules (a tool chip resolves, a
 * silent agent stops claiming progress) can be tested without mounting React.
 */
import type {
  ChatHubStreamEvent,
  ClientToolCall,
  ClientToolResult,
  ClientToolStatus,
  ToolActivityEntry,
  ToolActivityStatus,
  TurnOutcome,
} from './types';

export type { ToolActivityEntry, ToolActivityStatus, TurnOutcome };

export type SettledToolStatus = Exclude<ToolActivityStatus, 'running'>;

// The whole run is the trace, so the cap is a memory bound rather than a
// display choice: it has to be well above what a real turn calls, or the trace
// quietly drops the beginning of its own story. Five was sized for three chips.
export const TOOL_ACTIVITY_LIMIT = 24;

function lastIndexWhere(
  entries: ToolActivityEntry[],
  predicate: (entry: ToolActivityEntry) => boolean,
): number {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    if (predicate(entries[index])) return index;
  }
  return -1;
}

export function startTool(entries: ToolActivityEntry[], name: string): ToolActivityEntry[] {
  return [...entries, { name, status: 'running' as const }].slice(-TOOL_ACTIVITY_LIMIT);
}

function settledFrom(rawStatus: string): SettledToolStatus {
  return /error|fail|denied|refus|reject|cancel/i.test(rawStatus) ? 'error' : 'done';
}

/**
 * Resolve the chip a `tool_result` belongs to.
 *
 * Eve names the tool on both boundaries, so the name match is the primary key;
 * the newest running chip is the fallback for the paths that do not. A result
 * for a tool whose start never arrived still earns a chip, already resolved,
 * rather than being dropped.
 */
export function resolveTool(
  entries: ToolActivityEntry[],
  name: string | undefined,
  rawStatus: string,
  detail?: string,
): ToolActivityEntry[] {
  const status = settledFrom(rawStatus);
  // A reason rides only on a failure. A finished tool needs no excuse, and
  // attaching one anyway would dress success in error clothing.
  const reason = status === 'error' && detail?.trim() ? detail.trim() : undefined;
  const named = name
    ? lastIndexWhere(entries, (entry) => entry.status === 'running' && entry.name === name)
    : -1;
  const index = named >= 0 ? named : lastIndexWhere(entries, (entry) => entry.status === 'running');
  if (index < 0) {
    return name
      ? [...entries, { name, status, ...(reason ? { detail: reason } : {}) }].slice(-TOOL_ACTIVITY_LIMIT)
      : entries;
  }
  const next = entries.slice();
  next[index] = { ...next[index], status, ...(reason ? { detail: reason } : {}) };
  return next;
}

/** No chip may still pulse once the turn is over. */
export function settleTools(
  entries: ToolActivityEntry[],
  status: SettledToolStatus,
): ToolActivityEntry[] {
  if (!entries.some((entry) => entry.status === 'running')) return entries;
  return entries.map((entry) => (entry.status === 'running' ? { ...entry, status } : entry));
}

/**
 * The mark on a trace row is the only thing separating a running tool from a
 * finished one, and a mark is not a word. This is that word, for the screen
 * reader. Lives here rather than on either surface because both render it.
 */
export function toolStatusWord(status: ToolActivityStatus): string {
  if (status === 'running') return 'running';
  if (status === 'error') return 'failed';
  if (status === 'stopped') return 'stopped';
  return 'finished';
}

// The backend beats every 12s while Eve is quiet (HEARTBEAT_SECS in
// emjae_agent_client.py). A beat proves the connection is alive and nothing
// else, so it is counted rather than treated as progress.
export const HEARTBEAT_INTERVAL_MS = 12_000;

// Three beats of true silence. Below this a quiet gap is ordinary and saying
// "Still working" costs nothing; above it the claim is unbacked.
export const SILENT_HEARTBEATS_BEFORE_DOUBT = 3;

// Fifteen beats, three minutes, no event from Eve. Long enough that a slow tool
// step survives, short enough that a wedged turn ends on its own.
export const STALL_HEARTBEAT_LIMIT = 15;

export function isHeartbeatEvent(event: ChatHubStreamEvent): boolean {
  if (event.type !== 'thinking') return false;
  const data = event.data;
  if (!data) return false;
  // `heartbeat` is the stated contract; `eve_type` is read too so a frontend
  // deployed ahead of the backend still counts beats instead of trusting them.
  return data.heartbeat === true || data.eve_type === 'core.heartbeat';
}

function silenceFor(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 120) return `${seconds}s`;
  return `${Math.round(seconds / 60)}m`;
}

/**
 * The status line for a run of consecutive heartbeats, or null while the run is
 * still short enough that the backend's own wording is fair.
 */
export function silenceStatus(consecutiveHeartbeats: number): string | null {
  if (consecutiveHeartbeats < SILENT_HEARTBEATS_BEFORE_DOUBT) return null;
  return `I'm waiting, no update for ${silenceFor(consecutiveHeartbeats * HEARTBEAT_INTERVAL_MS)}`;
}

export const STALL_MESSAGE = `I got no update for ${silenceFor(
  STALL_HEARTBEAT_LIMIT * HEARTBEAT_INTERVAL_MS,
)}, so I stopped waiting.`;

export const STOPPED_WITH_PARTIAL = 'You stopped me before I finished.';
export const STOPPED_WITH_NOTHING = 'You stopped me before I answered.';

export function stoppedContent(partial: string): string {
  const trimmed = partial.trim();
  return trimmed ? `${trimmed}\n\n${STOPPED_WITH_PARTIAL}` : STOPPED_WITH_NOTHING;
}

/** What a parked turn is waiting on, in the user's terms. */
export function awaitingLabel(awaiting: string): string {
  if (awaiting === 'authorization.required') return "I'm waiting on an account connection";
  // A page tool parks the turn on the BROWSER, not on the user. Telling them
  // it waits on their reply would ask them for something nobody wants.
  if (awaiting === 'client_tool') return "I'm reading the page";
  return 'Waiting on your reply';
}

// ── The turn's wall clock ───────────────────────────────────────────────
//
// Every other progress signal on this surface is the agent's claim about
// itself. The clock is the one thing on screen that cannot be wrong, which is
// exactly what a turn legitimately running for minutes needs beside it.

/** Tenths under a minute, minutes and tenths above. Render it tabular. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, ms) / 1000;
  if (total < 60) return `${total.toFixed(1)}s`;
  return `${Math.floor(total / 60)}m ${(total % 60).toFixed(1)}s`;
}

/** The same clock rounded to whole seconds, for prose rather than a readout. */
function coarseElapsed(ms: number): string {
  const seconds = Math.round(Math.max(0, ms) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
}

function toolCountLabel(count: number): string {
  return count === 1 ? '1 tool' : `${count} tools`;
}

/**
 * The settled label that replaces the shimmer once the turn is over.
 *
 * It states the duration because that is the claim the elapsed timer was making
 * all along, and it names the ending because "Done" over a capped or abandoned
 * turn is the same lie as a chip that never resolves.
 */
export function traceSummary(
  outcome: TurnOutcome,
  toolCount: number,
  durationMs: number | null,
  awaiting?: string,
): string {
  const took = durationMs === null ? '' : coarseElapsed(durationMs);
  if (outcome === 'awaiting') {
    // A parked turn is not over, so no duration: stating one would read as an
    // ending, and the whole point of this outcome is that there isn't one yet.
    const ranFirst = toolCount > 0 ? ` · ${toolCountLabel(toolCount)}` : '';
    return `${awaitingLabel(awaiting ?? '')}${ranFirst}`;
  }
  // The tool count hangs off a separator rather than a second preposition:
  // "Stopped after 8s after 1 tool" is what reads out of the obvious phrasing.
  const ran = toolCount > 0 ? ` · ${toolCountLabel(toolCount)}` : '';
  if (outcome === 'capped') return `I hit the time limit${took ? ` at ${took}` : ''}${ran}`;
  if (outcome === 'stopped') return `Stopped${took ? ` after ${took}` : ''}${ran}`;
  if (outcome === 'error') return `I hit an error${took ? ` after ${took}` : ''}${ran}`;
  if (toolCount > 0) return `I ran ${toolCountLabel(toolCount)}${took ? ` in ${took}` : ''}`;
  return took ? `I worked for ${took}` : 'I answered';
}

/**
 * Whether a settled turn's trace is worth showing at all.
 *
 * An answer that called no tools and ended normally has nothing to add, and a
 * "Worked for 3s" line over every reply is noise wearing transparency's coat.
 */
export function hasSettledTrace(message: {
  trace?: ToolActivityEntry[];
  traceOutcome?: TurnOutcome;
}): boolean {
  if (message.trace?.length) return true;
  return Boolean(message.traceOutcome && message.traceOutcome !== 'done');
}

// The backend ends an over-long turn with a `complete` carrying the partial
// answer and this marker (chat_hub.py, `_EMJAE_TURN_MAX_SECS`). It is a short
// answer, not a failure, and must not render as one.
export function isTurnCapped(data: ChatHubStreamEvent['data']): boolean {
  return data?.stopped_by === 'turn_cap';
}

export function turnCapStatus(limitSecs: number | undefined): string {
  return limitSecs
    ? `I stopped at the ${coarseElapsed(limitSecs * 1000)} limit`
    : 'I stopped at the time limit';
}

// ── Client-executed page tools: the pure machine ────────────────────────
//
// Three states, terminal states absorbing, results posted exactly once:
//
//   tool_use ─→ pending ──deadline──→ expired   (error result, posted once)
//       │           │ page answers        ▲
//       │           ▼                     │
//       │        resolved (posted once)   │
//       └─ orphaned at arrival ───────────┘     expired is never retried
//
// Pure functions over an array, like the tool-activity rules above, so the
// contract is testable without React, timers, or a page to execute anything.

// Twenty seconds: long enough for a page busy hydrating, short enough that a
// turn parked on a dead tab ends instead of hanging the conversation.
export const CLIENT_TOOL_TIMEOUT_MS = 20_000;

export interface ClientToolTransition {
  calls: ClientToolCall[];
  /** The result this transition OWES the wire, or null. The caller posts it
   *  and nothing else does — this flag is the exactly-once guarantee. */
  post: ClientToolResult | null;
}

function clientToolResult(
  call: ClientToolCall,
  status: ClientToolStatus,
  reason: 'client_tool_timeout' | 'client_tool_orphaned' | null,
  content?: unknown,
): ClientToolResult {
  if (status === 'expired') {
    return {
      tool_use_id: call.id,
      status: 'error',
      error: reason ?? 'client_tool_timeout',
      message:
        reason === 'client_tool_orphaned'
          ? 'This tool call belongs to a turn that no longer exists.'
          : `The page did not answer within ${Math.round(CLIENT_TOOL_TIMEOUT_MS / 1000)} seconds.`,
    };
  }
  return { tool_use_id: call.id, status: 'success', content };
}

/**
 * A tool_use arrives from the stream. Orphans — a call for a turn that no
 * longer exists, or a session rebuilt from history replaying old calls — are
 * born expired: answered with an error immediately and never retried, because
 * re-running a page action whose turn is gone is how a stale tab edits today's
 * work.
 *
 * A duplicate id changes nothing and owes nothing: whatever answer that id got
 * the first time stands.
 */
export function beginClientTool(
  calls: ClientToolCall[],
  toolUse: { id: string; name: string; args: Record<string, unknown> },
  now: number,
  hasLiveTurn: boolean,
): ClientToolTransition {
  if (calls.some((call) => call.id === toolUse.id)) {
    return { calls, post: null };
  }
  const call: ClientToolCall = {
    ...toolUse,
    issuedAt: now,
    status: hasLiveTurn ? 'pending' : 'expired',
  };
  return {
    calls: [...calls, call],
    post: hasLiveTurn ? null : clientToolResult(call, 'expired', 'client_tool_orphaned'),
  };
}

/**
 * A pending call reaches a terminal state — the page answered (`resolved`,
 * with its content) or the deadline passed (`expired`). Only the transition
 * OUT of pending owes a post; settling an already-terminal call is a no-op,
 * which is what makes the executor-vs-deadline race safe: the loser calls
 * this, gets `post: null`, and goes home.
 */
export function settleClientTool(
  calls: ClientToolCall[],
  id: string,
  status: Exclude<ClientToolStatus, 'pending'>,
  outcome?: { content?: unknown; failed?: string },
): ClientToolTransition {
  const index = calls.findIndex((call) => call.id === id);
  if (index < 0 || calls[index].status !== 'pending') {
    return { calls, post: null };
  }
  const next = calls.slice();
  next[index] = { ...next[index], status };
  const call = next[index];
  // The page answering "it failed" is still an answer — resolved, with an
  // error result. Only silence (expired) means nobody answered.
  const post: ClientToolResult =
    status === 'expired'
      ? clientToolResult(call, 'expired', 'client_tool_timeout')
      : outcome?.failed !== undefined
        ? {
            tool_use_id: call.id,
            status: 'error',
            error: 'client_tool_failed',
            message: outcome.failed,
          }
        : { tool_use_id: call.id, status: 'success', content: outcome?.content };
  return { calls: next, post };
}

/** When a pending call's deadline falls due. */
export function clientToolDeadline(call: ClientToolCall): number {
  return call.issuedAt + CLIENT_TOOL_TIMEOUT_MS;
}
